/**
 * Proof chips on the device page (trust audit Phase 2, slice 2; mockup
 * https://claude.ai/artifact/F74A1dCrf6HQVPiJBL9XHE, "1. Device page").
 *
 * On for everyone; the per-person "How sure" switch (`trust.proof-chips`) is
 * an opt-out. Turned off, nothing is fetched and the page is exactly as before.
 * The server decides every label (GET /api/assets/:id/proof,
 * services/deviceProof.ts); this file only draws it.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useProofChipsSwitch, type Proof, type ProofFix } from "@/lib/proof";
import { ProofChip } from "@/components/proof/proof-chip";
import { ProofChipsHelp, ProofChipsToggle } from "@/components/proof/proof-help";
import { TaskCreateDialog } from "@/components/task-create-dialog";

export interface DeviceProof {
    assetId: string;
    due: Proof | null;
    tests: Record<string, Proof>;
}

export function deviceProofKey(assetId: string | undefined) {
    return ["asset-proof", assetId] as const;
}

export function useDeviceProof(assetId: string | undefined) {
    const sw = useProofChipsSwitch();
    const q = useQuery({
        queryKey: deviceProofKey(assetId),
        queryFn: async () => (await api.get<{ proof: DeviceProof }>(`/api/assets/${assetId}/proof`)).proof,
        enabled: !!assetId && sw.on,
        staleTime: 30_000,
        retry: false,
    });
    return {
        allowed: sw.allowed,
        on: sw.on,
        set: sw.set,
        // A failed read just means no chips: the page never depends on them.
        proof: sw.on && q.data ? q.data : null,
    };
}

/** "ⓘ What the chips mean · How sure: Off | On", under the at-a-glance tiles. */
export function DeviceProofControls({ on, set }: { on: boolean; set: (on: boolean) => void }) {
    return (
        <div className="flex flex-wrap items-center justify-end gap-2" data-testid="device-proof-controls">
            {on && <ProofChipsHelp />}
            <ProofChipsToggle on={on} onChange={set} />
        </div>
    );
}

/**
 * A history row's chip. "Ask the tech" opens a task the office writes
 * itself, linked to the device and the job (nothing is sent on its own).
 */
export function TestProofChip({
    proof,
    assetId,
    assetLabel,
    jobId,
}: {
    proof: Proof;
    assetId: string;
    assetLabel: string;
    jobId: string | null;
}) {
    const [asking, setAsking] = useState(false);
    const onFix = (fix: ProofFix) => {
        if (fix.action === "ask_tech") setAsking(true);
    };
    return (
        <>
            <ProofChip proof={proof} place="busy" onFix={onFix} testId="test-proof-chip" />
            {asking && (
                <TaskCreateDialog
                    open={asking}
                    onOpenChange={setAsking}
                    prefill={{
                        title: `Please type the readings off the signed paper: ${assetLabel}`,
                        links: [
                            { entityType: "asset", entityId: assetId, label: assetLabel },
                            ...(jobId ? [{ entityType: "job", entityId: jobId, label: "Job" }] : []),
                        ],
                    }}
                />
            )}
        </>
    );
}
